import {makeAutoObservable} from 'mobx'
import {Lang} from 'yougile-lang'
import {DataObjectId, Hub} from 'yougile-model'
import {backgroundImages} from '@client/background-images'
import {Sync} from '@client/sync'
import {FileChooser} from '@common/file-chooser'
import {Tracking, TrackingEvent} from '@common/tracking'
import {Skins} from '@components/account/system-appearance/system-appearance-store'
import {boardSettingsStore} from '@components/board/board-settings/board-settings-store'
import {BoardTabBg} from '@components/board/board-tab-bg'
import {showConfirm} from '@components/common/base/notifications'
import {createUseStore} from '@containers/hooks'
import {ThemeUtils} from '@extensions/theme-utils'

interface State {
  tabBgEnabled: boolean
  tabBgSkin: string
  tabBgShowMore: boolean
  _skins: Skins[]
  unsplashBgSkinRegular?: string
}

export class BoardAppearanceStore {
  state: State = {
    tabBgEnabled: false,
    tabBgSkin: '',
    tabBgShowMore: false,
    _skins: []
  }
  boardId: DataObjectId

  constructor(boardId: DataObjectId) {
    makeAutoObservable(this, {}, {autoBind: true})
    this.boardId = boardId
  }

  get skins(): Skins[] {
    if (!this.state.tabBgShowMore) {
      return this.state._skins.slice(0, 15)
    }
    return this.state._skins
  }

  syncNew(): void {
    this.updateFromBoard()
    this.calculateCustomSkins()
  }

  setTabBgEnabled(val: boolean): void {
    this.state.tabBgEnabled = val

    const board = getBoardById(this.boardId)
    if (board && board.data?.tabBgEnabled !== val) {
      board.data ??= {}
      board.data.tabBgEnabled = val
      board.commit()

      this.updateBackground()
    }

    if (val) {
      void Tracking.send(TrackingEvent.boardSettingsCustomBackgroundEnable)
    } else {
      void Tracking.send(TrackingEvent.boardSettingsCustomBackgroundDisable)
    }
  }

  async setTabBgSkin(skin: string): Promise<void> {
    const currSkin = BoardTabBg.getSkin()
    const user = Sync.getCurrentUser()
    const userHasThisSkin =
      user.customSkins.some(s => s === currSkin) ||
      backgroundImages.some(s => s === currSkin)

    const msg = Lang.msg.boardAppearance.confirmAreYouSureChangeBg
    const needApply =
      userHasThisSkin || (!userHasThisSkin && (await showConfirm(msg)))

    if (!needApply) {
      return
    }

    this.state.tabBgSkin = skin

    const board = getBoardById(this.boardId)

    if (board && board.data?.tabBgSkin !== skin) {
      board.data ??= {}
      board.data.tabBgSkin = skin
      board.commit()
      this.updateBackground()
    }
  }

  updateBackground(): void {
    void BoardTabBg.refresh()
    void ThemeUtils.upTheme()
    boardSettingsStore.updateBgTabModified()
  }

  setUnsplashBgSkinRegular(skin: string): void {
    this.state.unsplashBgSkinRegular = skin
  }

  loadBg = (event: React.MouseEvent): void => {
    void FileChooser.uploadImage(event).then(({url}) => {
      const user = Sync.getCurrentUser()
      user.customSkins.push(url)
      user.commit()

      this.calculateCustomSkins()
    })
  }

  tabBgShowMore(): void {
    this.state.tabBgShowMore = true
  }

  calculateCustomSkins(): void {
    const user = Sync.getCurrentUser()
    const customSkins = [...user.customSkins].reverse().map(skin => ({
      custom: true,
      path: skin
    }))

    const defaultSkins = backgroundImages.map(skin => ({
      custom: false,
      path: skin
    }))

    this.state._skins = [...customSkins, ...defaultSkins]
  }

  private updateFromBoard(): void {
    const board = getBoardById(this.boardId)
    if (!board) {
      return
    }

    const tabBgEnabled = !!board.data?.tabBgEnabled
    this.state.tabBgEnabled = tabBgEnabled
    if (!tabBgEnabled) {
      return
    }

    const tabBgSkin = board.data?.tabBgSkin
    if (tabBgSkin === undefined) {
      return
    }

    if (this.state.tabBgSkin !== tabBgSkin) {
      this.state.tabBgSkin = tabBgSkin
    }
  }

  deleteSkin = async (skin: string, e: React.MouseEvent): Promise<void> => {
    e.stopPropagation()
    const user = Sync.getCurrentUser()
    if (!(await showConfirm(Lang.msg.confirmDeleteBg))) {
      return
    }

    if (user.skin === skin) {
      user.skin = backgroundImages[9]
    }
    const ix = user.customSkins.indexOf(skin)
    if (ix > -1) {
      user.customSkins.splice(ix, 1)
    }

    user.commit()

    const board = getBoardById(this.boardId)
    if (board && board.data?.tabBgSkin === skin) {
      board.data ??= {}
      board.data.tabBgSkin = undefined
      board.commit()
      this.updateBackground()
    }
  }
}

const [BoardAppearanceContext, useBoardAppearanceStore] =
  createUseStore(BoardAppearanceStore)
export {BoardAppearanceContext, useBoardAppearanceStore}

export const getBoardById = (boardId: DataObjectId): Hub | undefined => {
  if (!boardId) {
    return undefined
  }
  return Sync.getCurrentSandbox().getHub(boardId)
}
